import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { Trophy, ChevronRight, Star, Globe, Crown, Shield, Sparkles, Calendar } from 'lucide-react';

const API_BASE = '';

const CATEGORY_STYLE = {
  european_club: { icon: Star, gradient: 'from-blue-950 to-indigo-950', accent: 'blue', border: 'border-blue-500/20', badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  international: { icon: Globe, gradient: 'from-amber-950 to-yellow-950', accent: 'amber', border: 'border-amber-500/20', badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  national_cups: { icon: Crown, gradient: 'from-emerald-950 to-teal-950', accent: 'emerald', border: 'border-emerald-500/20', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  super_cups: { icon: Sparkles, gradient: 'from-violet-950 to-purple-950', accent: 'violet', border: 'border-violet-500/20', badge: 'bg-violet-500/10 text-violet-400 border-violet-500/20' },
  domestic_leagues: { icon: Shield, gradient: 'from-slate-900 to-slate-800', accent: 'slate', border: 'border-slate-700/40', badge: 'bg-slate-500/10 text-slate-400 border-slate-600/20' },
};

const TIER_COLORS = {
  elite: 'ring-2 ring-amber-500/30 shadow-[0_0_15px_rgba(245,158,11,0.1)]',
  major: 'ring-1 ring-slate-600/50',
  standard: '',
  prestige: 'ring-1 ring-violet-500/30',
};

function CupCard({ cup, lang }) {
  const { t } = useTranslation();
  const isTR = lang === 'tr';
  const cupPath = isTR ? 'kupa' : 'cup';
  const leaguePath = cup.type === 'tournament' && cup.id === 'world-cup-2026'
    ? `/${lang}/${isTR ? 'dunya-kupasi-2026' : 'world-cup-2026'}`
    : cup.sport_id === 'football' && cup.slug
    ? `/${lang}/${isTR ? 'futbol' : 'football'}/${cup.slug}`
    : `/${lang}/${cupPath}/${cup.id}`;

  return (
    <Link to={leaguePath} data-testid={`cup-${cup.id}`}
      className={`group relative bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-2xl p-5 transition-all duration-300 hover:border-amber-500/40 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(245,158,11,0.08)] ${TIER_COLORS[cup.tier] || ''}`}>
      {cup.tier === 'elite' && (
        <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
          <Star className="w-3 h-3 text-slate-950 fill-slate-950" />
        </div>
      )}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform overflow-hidden">
          <img src={cup.logo_url} alt={cup.name} className="w-10 h-10 object-contain" loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-white truncate group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
            {cup.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-slate-500">{cup.country}</span>
            {cup.matches_count > 0 && (
              <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded font-semibold">
                {cup.matches_count} {t('cups.matches', 'matches')}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400 transition-colors shrink-0" />
      </div>
    </Link>
  );
}

export default function CupsPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isTR = currentLang === 'tr';

  useEffect(() => {
    fetch(`${API_BASE}/api/cups`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { categories, total } = data;
  const categoryOrder = ['european_club', 'international', 'national_cups', 'super_cups', 'domestic_leagues'];

  return (
    <div data-testid="cups-page">
      <SEOHead
        title={t('cups.cupsTitle') + ' | ODDSIS'}
        description={t('cups.cupsDesc')}
      />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #1a0a2e 30%, #2d1b4e 50%, #1a0a2e 70%, #020617 100%)' }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(245,158,11,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59,130,246,0.1) 0%, transparent 50%)' }} />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-10 md:py-16">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-amber-400">{t('cups.cupsTitle').split(' ').slice(0,2).join(' ')}</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Trophy className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('cups.cupsTitle').toUpperCase()}
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                {total} {t('cups.cupsDesc').split('.')[0]} - {t('cups.cupsDesc')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            {categoryOrder.filter(k => categories[k]).map(key => {
              const cat = categories[key];
              const style = CATEGORY_STYLE[key];
              const Icon = style.icon;
              return (
                <a key={key} href={`#${key}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-semibold transition-all hover:scale-105 ${style.badge}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {t(`cups.${key === 'european_club' ? 'europeanClub' : key === 'national_cups' ? 'nationalCups' : key === 'super_cups' ? 'superCups' : key === 'domestic_leagues' ? 'domesticLeagues' : 'international'}`, cat.title)}
                  <span className="text-[10px] opacity-60">({cat.items.length})</span>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 max-w-7xl py-8 space-y-10">
        {categoryOrder.filter(k => categories[k]).map(key => {
          const cat = categories[key];
          const style = CATEGORY_STYLE[key];
          const Icon = style.icon;

          return (
            <section key={key} id={key} data-testid={`category-${key}`}>
              <div className={`rounded-2xl overflow-hidden border ${style.border}`}>
                {/* Category Header */}
                <div className={`bg-gradient-to-r ${style.gradient} p-5 md:p-6`}>
                  <div className="flex items-center gap-3">
                    <Icon className="w-6 h-6 text-white/60" />
                    <h2 className="text-lg md:text-xl font-black text-white uppercase tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                      {t(`cups.${key === 'european_club' ? 'europeanClub' : key === 'national_cups' ? 'nationalCups' : key === 'super_cups' ? 'superCups' : key === 'domestic_leagues' ? 'domesticLeagues' : 'international'}`, cat.title)}
                    </h2>
                    <span className="text-xs bg-white/10 text-white/60 px-2.5 py-1 rounded-full font-semibold">
                      {cat.items.length}
                    </span>
                  </div>
                </div>

                {/* Cup Cards Grid */}
                <div className="p-4 md:p-6 bg-slate-950/50">
                  <div className={`grid gap-3 ${key === 'domestic_leagues' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                    {cat.items.map(cup => (
                      <CupCard key={cup.id} cup={cup} lang={currentLang} />
                    ))}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
